import { useEffect, useState } from 'react'

import { Section, Text } from '@factorialco/deprecated-design-system'
import {
  SalaryAdvanceSettingsPageDocument,
  SaveSalaryAdvanceCompanySettingDocument,
  type SaveSalaryAdvanceCompanySettingMutation,
} from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  MAX_CLOSEOUT_DAY,
  MIN_CLOSEOUT_DAY,
  normalizeCapPercentage,
  toCloseoutDay,
  toRequestDeadlineDay,
} from 'modules/salary_advance/lib/companySettings'

type Props = {
  requestDeadlineDay: number
  serviceEnabled: boolean
  advanceCapMonthlyWagePercent: number
  canEdit: boolean
}

const ClosureDaySetting = ({
  requestDeadlineDay,
  serviceEnabled,
  advanceCapMonthlyWagePercent,
  canEdit,
}: Props) => {
  const closeoutDay = toCloseoutDay(requestDeadlineDay)
  const [selectedCloseoutDay, setSelectedCloseoutDay] = useState<number>(closeoutDay)

  const [saveCompanySetting, { loading }] = useMutation(SaveSalaryAdvanceCompanySettingDocument, {
    extractorFn: (payload: SaveSalaryAdvanceCompanySettingMutation) =>
      payload.salaryAdvanceMutations?.saveSalaryadvanceCompanySetting ?? { errors: [] },
    refetchQueries: [SalaryAdvanceSettingsPageDocument],
  })

  useEffect(() => {
    setSelectedCloseoutDay(closeoutDay)
  }, [closeoutDay])

  const isDirty = selectedCloseoutDay !== closeoutDay

  const handleSave = async () => {
    const result = await saveCompanySetting({
      variables: {
        requestDeadlineDay: toRequestDeadlineDay(selectedCloseoutDay),
        serviceEnabled,
        advanceCapMonthlyWagePercent: normalizeCapPercentage(advanceCapMonthlyWagePercent),
      },
    })
    const { success } = handleResult(result, () => {})

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  const handleChange = (newValue: string) => {
    const nextValue = Number(newValue)
    if (!Number.isNaN(nextValue)) {
      setSelectedCloseoutDay(nextValue)
    }
  }

  const options = Array.from({ length: MAX_CLOSEOUT_DAY - MIN_CLOSEOUT_DAY + 1 }, (_, i) => ({
    label: i18n.t('salary_advance.settings.closure_day.day_of_each_month', {
      day: MIN_CLOSEOUT_DAY + i,
    }),
    value: MIN_CLOSEOUT_DAY + i,
  }))

  return (
    <Section.Form
      illustration="clock-app"
      title={i18n.t('salary_advance.settings.closure_day.new_title')}
      description={i18n.t('salary_advance.settings.closure_day.new_description')}
      submitLabel={i18n.t('common.save')}
      isDirty={isDirty}
      onSubmit={handleSave}
      disabled={!canEdit || loading}
    >
      <Text.Normal weight="semibold" size="200">
        {i18n.t('salary_advance.settings.closure_day.new_date_label')}
      </Text.Normal>
      <Select
        none={i18n.t('common.select')}
        value={String(selectedCloseoutDay)}
        onChange={handleChange}
        options={options}
        missingLabel={i18n.t('common.missing_field')}
        readonly
      />
    </Section.Form>
  )
}

export default ClosureDaySetting
